import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { safeLocalPath, clearReturnTo } from "@/lib/returnTo";
import { usePageMeta } from "@/hooks/usePageMeta";

// Reads the one-time token and immediately removes it from the address bar.
export const useUrlToken = () => {
  const [token] = useState(() => new URLSearchParams(window.location.search).get("token") || "");
  useEffect(() => { window.history.replaceState(null, "", window.location.pathname); }, []);
  return token;
};

const Shell = ({ testid, title, children }) => (
  <div className="mx-auto max-w-md px-5 py-16 text-center text-[#F8F3E6] sm:px-8" data-testid={testid}>
    <h1 className="font-display text-3xl font-semibold">{title}</h1>
    <div className="mt-5 space-y-4 text-sm leading-relaxed text-[#A3A5B8]">{children}</div>
  </div>
);

export default function VerifyEmail() {
  usePageMeta({ title: "Confirm your email - The Cardlight", description: "Confirm your email address for The Cardlight.", path: "/auth/verify" });
  const token = useUrlToken();
  const navigate = useNavigate();
  const { completeSignIn, login } = useAuth();
  const [error, setError] = useState("");
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    (async () => {
      try {
        const res = await api.post("/auth/email/verify", { token });
        await completeSignIn(res.data.user, "email");
        clearReturnTo();
        navigate(safeLocalPath(res.data.return_to), { replace: true });
      } catch (e) {
        setError(typeof e?.response?.data?.detail === "string" ? e.response.data.detail : "This link is invalid or has expired.");
      }
    })();
  }, [token, completeSignIn, navigate]);

  return (
    <Shell testid="verify-page" title={error ? "Link not valid" : "Confirming your email…"}>
      {error && (
        <>
          <p data-testid="verify-error">{error}</p>
          <button onClick={login} className="rounded-full bg-[#D9A441] px-5 py-2.5 font-medium text-[#0B0C15]" data-testid="verify-signin-btn">Sign in or create an account</button>
        </>
      )}
    </Shell>
  );
}

export { Shell };
